import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { runProgress } from '../../services/workbook/estimate';
import type { IndexState } from '../../services/workbook/WorkbookIndexer';
import { ProgressBar } from './ProgressBar';

/** Where the workbook index stands, as the header's dot and the line under it say it. `warn`: the model pass
 *  failed — the chat stays open and the next message makes the server build it again (409 `index_failed` → a job
 *  start → the turn waits for the rebuild). */
export type WorkbookState = 'ready' | 'busy' | 'warn' | 'failed';

export function workbookState(index: IndexState): WorkbookState {
  if (index.phase === 'failed') return 'failed';
  if (index.phase === 'capturing' || index.phase === 'committing' || index.phase === 'understanding') return 'busy';
  const mp = index.status?.model_pass;
  if (mp?.state === 'running' || mp?.state === 'pending') return 'busy';
  if (mp?.state === 'failed') return 'warn';
  return 'ready';
}

/** The line under the header, only while there is something to say: the workbook being read or updated (with
 *  one progress bar, to the end of the server's pass), a pass to redo (amber) or
 *  an index that failed (red, with a Retry button). A workbook that is up to date has no line: the header's dot
 *  (`WorkbookDot`) says it. Blocks and sheet counts are the index's vocabulary, not the user's, and are not shown.
 *
 *  The index keeps itself fresh — a message sent after the user changed a sheet is refused as stale and sent again
 *  once the sheet is re-read, and the sheets the agent wrote in are re-read after its turn — so no refresh action
 *  sits here; the dot's menu keeps one for what a fingerprint does not see (formats, styles). */
export function IndexStatusLine({ index, onRetry }: { index: IndexState; onRetry: () => void }) {
  const { t } = useTranslation();
  const state = workbookState(index);
  const { fraction } = runProgress(index, index.size);

  if (state === 'failed') {
    return (
      <Line testId="index-status">
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-danger">{t('index.failed', { error: index.error ?? '' })}</span>
          <button type="button" onClick={onRetry} className="shrink-0 rounded-[5px] border border-line bg-white px-2.5 py-0.5 text-[12px] font-medium text-ink transition-colors hover:bg-hover">
            {t('index.retry')}
          </button>
        </div>
      </Line>
    );
  }
  if (state === 'warn') {
    // The server's own sentence when the workbook is the cause (past what one build may read): a message would
    // start the same build again, so the line says why instead of promising a re-read.
    const reason = index.status?.model_pass?.reason;
    return <Line testId="index-status"><span className="block truncate text-warn" title={reason ?? undefined}>{reason ?? t('index.understandLater')}</span></Line>;
  }
  if (state !== 'busy') return null;

  // A re-index that keeps the composer open is something happening beside you; one that shuts it is something
  // you are waiting on. A re-index is one run for the user, reading the changed sheets then the server's pass
  // over them: the indexer opens the chat as soon as the sheets are in (`understand`, version > 1), and the line
  // keeps the same words and the same bar to the end of the pass instead of starting a second one from zero.
  const reading = index.phase === 'capturing' || index.phase === 'committing' || index.phase === 'understanding';
  const progress = reading ? fraction : runProgress({ ...index, phase: 'understanding' }, index.size).fraction;
  const update = index.ready && (reading || (index.status?.version ?? 1) > 1);
  const text = update ? t('index.updating') : index.phase === 'capturing' || index.phase === 'committing' ? t('index.reading') : t('index.understandingRun');
  return (
    <Line testId="index-status">
      <span className="block truncate text-muted">{text}</span>
      <div className="mt-1">
        <ProgressBar fraction={progress} thin />
      </div>
    </Line>
  );
}

function Line({ testId, children }: { testId: string; children: ReactNode }) {
  return (
    <div className="border-b border-line-soft bg-hover-soft px-3.5 py-1.5 text-[12px]" role="status" aria-live="polite" data-testid={testId}>
      {children}
    </div>
  );
}

const DOT: Record<WorkbookState, string> = {
  ready: 'bg-accent',
  busy: 'bg-accent animate-pulse',
  warn: 'bg-warn',
  failed: 'bg-danger',
};

/** The header's dot beside the brand: green when the agent's picture of the workbook is up to date, pulsing while
 *  it is being updated, amber or red when something is to redo. Clicked, it opens one action — read the workbook
 *  again — for what the automatic refresh does not see. */
export function WorkbookDot({ index, onReread }: { index: IndexState; onReread: () => void }) {
  const { t } = useTranslation();
  const state = workbookState(index);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const title = t(`index.dot.${state}`);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={root} className="relative" data-testid="workbook-dot" data-state={state}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={state === 'busy'}
        title={title}
        aria-label={title}
        aria-expanded={open}
        className="grid h-7 w-7 place-items-center rounded-[5px] transition-colors hover:bg-hover-soft disabled:cursor-default disabled:hover:bg-transparent"
      >
        <span className={`block h-2 w-2 rounded-full ${DOT[state]}`} aria-hidden="true" />
      </button>
      {open && state !== 'busy' && (
        <div role="menu" className="absolute left-0 top-8 z-30 min-w-[200px] rounded-md border border-line bg-white py-1 shadow-lg">
          <div className="border-b border-line-soft px-3 pb-1.5 pt-1 text-[11.5px] text-muted">{title}</div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onReread();
            }}
            className="flex w-full px-3 py-1.5 text-left text-[12.5px] text-ink hover:bg-hover"
          >
            {t('index.reread')}
          </button>
        </div>
      )}
    </div>
  );
}
